import { format, subDays, startOfDay, endOfDay, parseISO, isWithinInterval } from 'date-fns';

/** Format seconds as M:SS for call duration. */
export function formatCallDuration(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(Number(seconds) / 60);
  const s = Math.floor(Number(seconds) % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function parseHistoryDate(createdAt) {
  if (!createdAt) return null;
  try {
    const d = typeof createdAt === 'string' && createdAt.includes('T') ? parseISO(createdAt) : new Date(createdAt);
    return isNaN(d.getTime()) ? null : d;
  } catch (_) {
    return null;
  }
}

export function getDateRange(dateFilter, customStart, customEnd) {
  const now = new Date();
  if (dateFilter === 'week') {
    return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
  }
  if (dateFilter === 'month') {
    return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
  }
  try {
    const start = startOfDay(parseISO(customStart));
    const end = endOfDay(parseISO(customEnd));
    return { start, end };
  } catch (_) {
    return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
  }
}

/** Sample wallet ledger (full history: used, blocked, recharge, unblocked, adjustment, activation) with balance. */
export function getSampleWalletLedger() {
  const now = new Date();
  const events = [
    { event_type: 'Credit Used', event_short: 'CU', credits_delta: -25, description: 'Credits Deducted for Agent Call', icon_color: '#94a3b8', id_suffix: '105' },
    { event_type: 'Credit Blocked', event_short: 'CB', credits_delta: 50, description: 'Credits Reserved for ongoing session', icon_color: '#f59e0b', id_suffix: '104' },
    { event_type: 'Recharge', event_short: 'RC', credits_delta: 2000, description: 'Credits Added via Recharge', icon_color: '#22c55e', id_suffix: '103' },
    { event_type: 'Credit Unblocked', event_short: 'CUB', credits_delta: 15, description: 'Credits Released from blocked state', icon_color: '#3b82f6', id_suffix: '102' },
    { event_type: 'Adjustment', event_short: 'ADJ', credits_delta: 100, description: 'Manual Adjustment by Admin', icon_color: '#f97316', id_suffix: '101' },
    { event_type: 'Wallet Activation', event_short: 'WA', credits_delta: 500, description: 'Initial Credits Added', icon_color: '#38bdf8', id_suffix: '100' },
  ];
  const out = [];
  let balance = 0;
  const dates = [
    [31, 21, 15], [31, 19, 40], [31, 0, 15], [30, 15, 30], [29, 22, 0], [25, 14, 30],
    [28, 10, 0], [27, 16, 45], [26, 9, 20], [24, 11, 0], [23, 14, 30], [22, 8, 0],
  ];
  events.forEach((ev, i) => {
    const [day, hour, min] = dates[i % dates.length];
    const d = new Date(now);
    d.setDate(d.getDate() - (now.getDate() - day));
    d.setHours(hour, min, 0, 0);
    balance += ev.credits_delta;
    out.push({
      id: `wh_${ev.id_suffix}`,
      created_at: d.toISOString(),
      transaction_type: ev.event_type,
      event_short: ev.event_short,
      voice_credits: Math.abs(ev.credits_delta),
      credits_delta: ev.credits_delta,
      description: ev.description,
      icon_color: ev.icon_color,
      balance: Math.max(0, balance),
    });
  });
  return out.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
}

/** Sample wallet history (credits added only) for backward compatibility. */
export function getSampleWalletHistory() {
  const ledger = getSampleWalletLedger();
  return ledger
    .filter((r) => ['Recharge', 'Wallet Activation', 'Refund', 'Credit Unblocked', 'Adjustment'].includes(r.transaction_type))
    .map(({ id, created_at, transaction_type, voice_credits, description, icon_color }) => ({
      id,
      created_at,
      transaction_type,
      voice_credits,
      description,
      badge_background_color: icon_color || '#2196F3',
      badge_text_color: '#FFFFFF',
    }));
}

/** Sample usage transactions: date-time, agent name, agent email, call-duration, credit used, balance. */
export function getSampleUsageTransactions() {
  const now = new Date();
  const agents = [
    { agent_name: 'Priya Sharma', agent_email: 'priya@example.com' },
    { agent_name: 'Rahul Verma', agent_email: 'rahul@example.com' },
    { agent_name: 'Anita Patel', agent_email: 'anita@example.com' },
    { agent_name: 'Vikram Singh', agent_email: 'vikram@example.com' },
    { agent_name: 'Sneha Reddy', agent_email: 'sneha@example.com' },
  ];
  let balance = 450;
  const out = [];
  for (let i = 0; i < 20; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - (14 - (i % 14)));
    d.setHours(9 + (i % 8), (i % 60) % 60, 0, 0);
    const creditUsed = [5, 8, 12, 6, 15, 10, 7, 11, 9, 14][i % 10];
    balance -= creditUsed;
    const durationSec = creditUsed * 60 + (i % 60);
    out.push({
      id: `usage-${i}`,
      date_time: d.toISOString(),
      agent_name: agents[i % agents.length].agent_name,
      agent_email: agents[i % agents.length].agent_email,
      call_duration: durationSec,
      credit_used: creditUsed,
      balance: Math.max(0, balance),
    });
  }
  return out.sort((a, b) => new Date(b.date_time) - new Date(a.date_time));
}

/** Sample voice credits bar chart data (used per day). */
export function getSampleVoiceCreditsBarData(start, end) {
  const days = [];
  let cursor = new Date(start);
  const perDay = [18, 25, 12, 32, 15, 28, 22, 14, 30, 19, 24, 11, 27, 16];
  let i = 0;
  while (cursor <= end) {
    days.push({
      period: format(cursor, 'MMM d'),
      used: perDay[i % perDay.length],
    });
    cursor.setDate(cursor.getDate() + 1);
    i += 1;
  }
  return days;
}

/** Sample user-wise credits for demo. */
export function getSampleUserWiseData() {
  return [
    { name: 'Priya Sharma', credits: 142 },
    { name: 'Rahul Verma', credits: 98 },
    { name: 'Anita Patel', credits: 87 },
    { name: 'Vikram Singh', credits: 65 },
    { name: 'Sneha Reddy', credits: 54 },
    { name: 'Arjun Nair', credits: 41 },
    { name: 'Kavita Joshi', credits: 28 },
    { name: 'Rohan Desai', credits: 19 },
  ];
}
